import { useState, useEffect, useRef } from "react";
import { useDocPrice, priceLabel } from "@/utils/docPrices";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LaurelStats from "@/components/LaurelStats";
import { FileText, Calculator, LayoutTemplate, Download, MapPin } from "lucide-react";
import { STATE_DATA, STATE_SLUGS } from "@/data/stateData";

// Same reveal hook the marketing pages use (threshold 0.05 per DESIGN.md)
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

const fade = (inView) =>
  `transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`;

const FAQS = (P) => [
  {
    q: "How does the paystub generator work?",
    a: `Enter your company, employee, and pay details, and MintSlip calculates federal tax, state tax, Social Security, Medicare, and year-to-date totals automatically. Preview the finished stub live, then ${P === "free" ? "download the PDF instantly, free" : `pay ${P} to download the PDF instantly`}.`,
  },
  {
    q: "Are the tax calculations accurate for my state?",
    a: "Yes, withholdings are calculated for the state you select, including states with no income tax. Pick your state below to see its rates and pay stub requirements.",
  },
  {
    q: "Do I need to create an account?",
    a: "No. There's no sign-up. Create your stub, pay, and the PDF downloads immediately with a copy sent to your email.",
  },
  {
    q: "Can I make more than one paystub?",
    a: `Yes, create as many as you need${P === "free" ? ", free" : ` at ${P} each`}. Each stub carries correct year-to-date totals based on your hire date and pay schedule.`,
  },
];

export default function PaystubGeneratorHub() {
  const navigate = useNavigate();
  // the pay stub price as the admin set it (Preferences > Document prices)
  const P = priceLabel(useDocPrice("paystub", 9.99));
  const [heroRef, heroInView] = useInView();
  const [benefitRef, benefitInView] = useInView();
  const [statesRef, statesInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS(P).map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  const appSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "MintSlip Paystub Generator",
    "description": "Create professional pay stubs online with automatic tax calculations for every US state",
    "url": "https://mintslip.com/paystub-generator",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": { "@type": "Offer", "price": "9.99", "priceCurrency": "USD" },
  };

  return (
    <>
      <Helmet>
        <title>Paystub Generator: Create Pay Stubs Online in Minutes | MintSlip</title>
        <meta name="description" content="Create professional pay stubs online with automatic federal and state tax calculations for all 50 states. Live preview, instant PDF download, no sign-up required." />
        <link rel="canonical" href="https://mintslip.com/paystub-generator" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mintslip.com/paystub-generator" />
        <meta property="og:title" content="Paystub Generator: Create Pay Stubs Online | MintSlip" />
        <meta property="og:description" content="Professional pay stubs with automatic tax calculations for all 50 states. Instant download, no sign-up." />
        <meta property="og:image" content="https://mintslip.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Paystub Generator: Create Pay Stubs Online | MintSlip" />
        <meta name="twitter:description" content="Professional pay stubs with automatic tax calculations for all 50 states. Instant download, no sign-up." />
        <meta name="twitter:image" content="https://mintslip.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Header title="MintSlip" />

      {/* Hero */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-10 md:pt-20 md:pb-14">
        <div className={`text-center ${fade(heroInView)}`}>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 rounded-full border border-emerald-200/80 text-sm font-medium text-emerald-900 mb-6">
            <FileText className="w-4 h-4 text-emerald-700" />
            Paystub Generator
          </div>
          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Create a paystub in <span className="font-black">minutes</span>
          </h1>
          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Accurate federal and state tax calculations for all 50 states, professional templates, and a live preview as you type.
          </p>
          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
          </div>
          <p className="text-sm text-slate-500 mb-8">Instant download. No sign-up required.</p>
          <LaurelStats />
        </div>
      </section>

      {/* Benefits — home bento tones */}
      <section ref={benefitRef} className="max-w-[1288px] mx-auto px-6 pb-14 md:pb-20">
        <div className={`grid grid-cols-1 md:grid-cols-3 gap-5 ${fade(benefitInView)}`}>
          <div className="rounded-[28px] bg-slate-100 p-7 md:p-8">
            <span className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
              <Calculator className="w-6 h-6 text-slate-900" />
            </span>
            <h3 className="font-display text-xl font-bold tracking-tight text-slate-900 mb-1.5">The math is done for you</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Federal tax, state tax, Social Security, Medicare, and year-to-date totals: every number calculated automatically for your state.
            </p>
          </div>
          <div className="rounded-[28px] bg-emerald-400 p-7 md:p-8">
            <span className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
              <LayoutTemplate className="w-6 h-6 text-emerald-700" />
            </span>
            <h3 className="font-display text-xl font-bold tracking-tight text-emerald-950 mb-1.5">Professional templates</h3>
            <p className="text-sm text-emerald-900/80 leading-relaxed">
              Clean, employer-style layouts with a live preview that updates as you type. What you see is exactly what downloads.
            </p>
          </div>
          <div className="rounded-[28px] bg-[#0b0b0b] p-7 md:p-8">
            <span className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mb-5">
              <Download className="w-6 h-6 text-emerald-400" />
            </span>
            <h3 className="font-display text-xl font-bold tracking-tight text-white mb-1.5">Instant download ({P === "free" ? "free" : P})</h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              Pay securely and your PDF downloads immediately, with a copy sent to your email. No account, no subscription.
            </p>
          </div>
        </div>
      </section>

      {/* State grid */}
      <section ref={statesRef} className="max-w-[1288px] mx-auto px-6 pb-14 md:pb-20">
        <div className={fade(statesInView)}>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mb-3">
            Pick your <span className="font-black">state</span>
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            Every state has its own tax rates and pay stub rules. Choose yours for the details, or jump straight into the generator.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {STATE_SLUGS.map((s) => (
              <Link
                key={s}
                to={`/paystub-generator/${s}`}
                className="group bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3.5 hover:border-emerald-400 hover:shadow-md transition-all"
              >
                <div className="font-display font-bold tracking-tight text-slate-900 group-hover:text-emerald-700 transition-colors">
                  {STATE_DATA[s].name}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {STATE_DATA[s].hasStateTax ? `${STATE_DATA[s].taxRate} tax` : "No state tax"}
                </div>
              </Link>
            ))}
            <Link
              to="/paystub-generator/canada"
              className="group bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3.5 hover:border-emerald-400 hover:shadow-md transition-all"
            >
              <div className="font-display font-bold tracking-tight text-slate-900 group-hover:text-emerald-700 transition-colors flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-emerald-700" />
                Canada
              </div>
              <div className="text-xs text-slate-500 mt-0.5">Provincial taxes</div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section ref={faqRef} className="max-w-[1288px] mx-auto px-6 pb-14 md:pb-20">
        <div className={fade(faqInView)}>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mb-8">
            Common <span className="font-black">questions</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FAQS(P).map((f) => (
              <div key={f.q} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-7">
                <h3 className="font-display font-bold tracking-tight text-slate-900 mb-2">{f.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section ref={ctaRef} className="max-w-[1288px] mx-auto px-6 pb-20 md:pb-28">
        <div className={`rounded-[28px] bg-slate-100 px-6 py-14 md:py-20 text-center ${fade(ctaInView)}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Your paystub is <span className="font-black">minutes away</span>
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Accurate withholdings for your state, professional templates, instant download.
          </p>
          <div className="flex justify-center">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
          </div>
        </div>
      </section>

      <Footer />
      </div>
    </>
  );
}
